import { useCallback, useEffect, useRef, useState } from "react"
import { createHashRouter } from "react-router-dom"
import {
    DisclaimerPage,
    EULAPage,
    PrivacyPolicyPage,
    TermsOfUsePage,
} from "@/pages/terms-and-policy/Terms"
import { RoutesPath } from "@/router/config"
import { loginRequest } from "@/config/authConfig"
import { useMsal } from "@azure/msal-react"
import { InteractionStatus } from "@azure/msal-browser"
import { lazyWithRetry } from "@/utils/lazyWithRetry"
import AuthUnavailable from "@/components/AuthUnavailable/AuthUnavailable"
import { getLoginRetryDelay, shouldRetryLogin } from "@/utils/authLoginRetry"

type LoginGuard = {
    owner: symbol
    instance: unknown
    generation: number
    releaseScheduled: boolean
}

let loginGuard: LoginGuard | null = null
let nextLoginGeneration = 0

const releaseLoginGuard = (guard: LoginGuard) => {
    if (loginGuard === guard) loginGuard = null
}

const scheduleLoginGuardRelease = (owner: symbol, guard: LoginGuard | null) => {
    if (!guard || loginGuard !== guard || guard.owner !== owner) return

    guard.releaseScheduled = true
    Promise.resolve().then(() => {
        if (loginGuard === guard && guard.releaseScheduled) {
            loginGuard = null
        }
    })
}

export const AutoLogin = () => {
    const { instance, inProgress } = useMsal()
    const [authUnavailable, setAuthUnavailable] = useState(false)
    const ownerRef = useRef<symbol | null>(null)
    const currentGuardRef = useRef<LoginGuard | null>(null)
    const mountedRef = useRef(false)
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const retryGenerationRef = useRef(0)
    const attemptRef = useRef(0)
    const startLoginRef = useRef<() => LoginGuard | null>(() => null)
    const authUnavailableRef = useRef(authUnavailable)
    const inProgressRef = useRef(inProgress)
    const instanceRef = useRef(instance)

    if (ownerRef.current === null) ownerRef.current = Symbol("AutoLogin")
    authUnavailableRef.current = authUnavailable
    inProgressRef.current = inProgress
    instanceRef.current = instance

    const canStartLogin = useCallback(() => {
        if (!mountedRef.current || authUnavailableRef.current) return false
        if (inProgressRef.current !== InteractionStatus.None) return false
        return instanceRef.current.getAllAccounts().length === 0
    }, [])

    const claimLoginGuard = useCallback(() => {
        const owner = ownerRef.current
        if (!owner) return null

        const currentGuard = loginGuard
        if (currentGuard) {
            if (currentGuard.owner === owner && currentGuard.instance === instanceRef.current) {
                currentGuard.releaseScheduled = false
                currentGuardRef.current = currentGuard
                return { guard: currentGuard, acquired: false }
            }
            if (!currentGuard.releaseScheduled) return null
            releaseLoginGuard(currentGuard)
        }

        const guard: LoginGuard = {
            owner,
            instance: instanceRef.current,
            generation: nextLoginGeneration++,
            releaseScheduled: false,
        }
        loginGuard = guard
        currentGuardRef.current = guard
        return { guard, acquired: true }
    }, [])

    const scheduleRetry = useCallback(
        (attempt: number) => {
            const retryGeneration = ++retryGenerationRef.current
            retryTimerRef.current = setTimeout(() => {
                if (retryGenerationRef.current !== retryGeneration) return
                retryTimerRef.current = null
                if (canStartLogin()) startLoginRef.current()
            }, getLoginRetryDelay(attempt))
        },
        [canStartLogin],
    )

    const startLogin = useCallback((): LoginGuard | null => {
        if (!canStartLogin()) return null

        const attempt = attemptRef.current
        const claimed = claimLoginGuard()
        if (!claimed) return null
        if (!claimed.acquired) return claimed.guard

        const guard = claimed.guard
        const generation = guard.generation
        const ownsCurrentGuard = () =>
            loginGuard === guard && loginGuard?.generation === generation
        const finishAttempt = () => {
            if (!ownsCurrentGuard()) return
            releaseLoginGuard(guard)
            if (currentGuardRef.current === guard) currentGuardRef.current = null
        }
        const handleFailure = () => {
            if (!ownsCurrentGuard()) return
            releaseLoginGuard(guard)
            if (currentGuardRef.current === guard) currentGuardRef.current = null
            if (!mountedRef.current || attemptRef.current !== attempt) return

            if (shouldRetryLogin(attempt)) {
                attemptRef.current = attempt + 1
                scheduleRetry(attempt)
            } else {
                setAuthUnavailable(true)
            }
        }

        try {
            Promise.resolve(instanceRef.current.loginRedirect(loginRequest))
                .then(finishAttempt)
                .catch(handleFailure)
        } catch {
            handleFailure()
        }

        return guard
    }, [canStartLogin, claimLoginGuard, scheduleRetry])
    startLoginRef.current = startLogin

    useEffect(() => {
        mountedRef.current = true
        const cleanup = () => {
            mountedRef.current = false
            retryGenerationRef.current += 1
            if (retryTimerRef.current !== null) {
                clearTimeout(retryTimerRef.current)
                retryTimerRef.current = null
            }
            if (ownerRef.current) scheduleLoginGuardRelease(ownerRef.current, currentGuardRef.current)
        }

        const eligible = canStartLogin()
        const currentGuard = loginGuard
        if (currentGuard?.owner === ownerRef.current) {
            if (currentGuard.instance !== instance) {
                releaseLoginGuard(currentGuard)
                if (currentGuardRef.current === currentGuard) currentGuardRef.current = null
            } else {
                currentGuard.releaseScheduled = false
                currentGuardRef.current = currentGuard
            }
        }

        // Wait until MSAL has finished processing any in-flight interaction
        // (e.g. the B2C redirect callback). Triggering loginRedirect() while a
        // redirect is still being handled causes a login loop.
        if (!eligible) return cleanup
        retryGenerationRef.current += 1
        if (retryTimerRef.current !== null) {
            clearTimeout(retryTimerRef.current)
            retryTimerRef.current = null
        }
        startLogin()

        return cleanup
    }, [instance, inProgress, authUnavailable, canStartLogin, startLogin])

    const handleRetry = () => {
        attemptRef.current = 0
        authUnavailableRef.current = false
        setAuthUnavailable(false)
        startLogin()
    }

    // Minimal full-height placeholder while the redirect is in flight.
    if (authUnavailable) return <AuthUnavailable onRetry={handleRetry} />
    return <div style={{ height: "100vh" }} />
}

const MobileOopsPage = lazyWithRetry(() => import("@/pages/mobileOops"))
const DeploymentPage = lazyWithRetry(() => import("@/pages/deployment"))

const unauthenticatedRouter = createHashRouter([
    {
        path: RoutesPath.TermsOfUse,
        element: <TermsOfUsePage />,
    },
    {
        path: RoutesPath.TermsOfService,
        element: <TermsOfUsePage />,
    },
    {
        path: RoutesPath.PrivacyPolicy,
        element: <PrivacyPolicyPage />,
    },
    {
        path: RoutesPath.Disclaimer,
        element: <DisclaimerPage />,
    },
    {
        path: RoutesPath.EULA,
        element: <EULAPage />,
    },
    {
        path: "/mobile-oops",
        element: <MobileOopsPage />,
    },
    {
        path: RoutesPath.Deployment,
        element: <DeploymentPage />,
    },
    {
        path: "*",
        element: <AutoLogin />,
    },
])

export default unauthenticatedRouter
